/* Red Cat Creations theme.js
   Vanilla JS, no dependencies. Handles:
   - Drawer open/close (mobile nav, categories)
   - Sidebar group collapse/expand (aria-expanded)
   - Product tab switching (ARIA tablist)
   - Quantity input controls
   - Escape key + overlay click closes drawers
   - Collection sort dropdown
   - Product media thumb swap
*/
(function () {
  'use strict';

  const doc = document;
  const body = doc.body;

  /* ============== Header offset (so the sticky sidebar rail sits under the header) ============== */
  function updateHeaderOffset() {
    const header = doc.querySelector('[data-site-header]') || doc.querySelector('.site-header');
    const announcement = doc.querySelector('.announcement-bar');
    let offset = 0;
    if (header) offset += header.offsetHeight;
    if (announcement && header && !header.contains(announcement)) offset += announcement.offsetHeight;
    doc.documentElement.style.setProperty('--header-offset', offset + 'px');
  }
  updateHeaderOffset();
  window.addEventListener('load', updateHeaderOffset);
  window.addEventListener('resize', updateHeaderOffset);

  /* ============== Drawers ============== */
  const overlay = doc.querySelector('[data-drawer-overlay]');
  const drawers = Array.from(doc.querySelectorAll('[data-drawer]'));
  const triggers = Array.from(doc.querySelectorAll('[data-drawer-trigger]'));

  function openDrawer(name) {
    const drawer = doc.querySelector('[data-drawer="' + name + '"]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('hidden');
    if (overlay) {
      overlay.hidden = false;
      requestAnimationFrame(function () {
        overlay.classList.add('is-active');
      });
    }
    body.style.overflow = 'hidden';
    // Sync triggers
    triggers.forEach(function (btn) {
      if (btn.getAttribute('data-drawer-trigger') === name) {
        btn.setAttribute('aria-expanded', 'true');
      }
    });
    // Focus first focusable
    const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) focusable.focus();
  }

  function closeDrawer(drawer) {
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      const name = drawer.getAttribute('data-drawer');
      triggers.forEach(function (btn) {
        if (btn.getAttribute('data-drawer-trigger') === name) {
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    }
    closeAllDrawersFinish();
  }

  function closeAllDrawers() {
    drawers.forEach(function (d) {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    triggers.forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    closeAllDrawersFinish();
  }

  function closeAllDrawersFinish() {
    if (overlay) {
      overlay.classList.remove('is-active');
      setTimeout(function () {
        if (!doc.querySelector('[data-drawer].is-open')) overlay.hidden = true;
      }, 220);
    }
    body.style.overflow = '';
  }

  triggers.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer(btn.getAttribute('data-drawer-trigger'));
    });
  });

  Array.from(doc.querySelectorAll('[data-drawer-close]')).forEach(function (btn) {
    btn.addEventListener('click', function () {
      const drawer = btn.closest('[data-drawer]');
      closeDrawer(drawer);
    });
  });

  if (overlay) {
    overlay.addEventListener('click', closeAllDrawers);
  }

  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeAllDrawers();
    }
  });

  /* ============== Collapsible toggles (sidebar groups) ============== */
  Array.from(doc.querySelectorAll('[data-collapse-toggle]')).forEach(function (btn) {
    btn.addEventListener('click', function () {
      const targetId = btn.getAttribute('aria-controls');
      const target = targetId ? doc.getElementById(targetId) : null;
      if (!target) return;
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (expanded) {
        target.setAttribute('hidden', '');
      } else {
        target.removeAttribute('hidden');
      }
    });
  });

  /* ============== Tabbed products ============== */
  Array.from(doc.querySelectorAll('[data-tabbed-products]')).forEach(function (section) {
    const tabs = Array.from(section.querySelectorAll('[data-tab-trigger]'));
    const panels = Array.from(section.querySelectorAll('[data-tab-panel]'));

    function activate(index) {
      tabs.forEach(function (t, i) {
        const active = i === index;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach(function (p, i) {
        const active = i === index;
        p.classList.toggle('is-hidden', !active);
        if (active) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { activate(i); });
      tab.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const next = (i + 1) % tabs.length;
          tabs[next].focus();
          activate(next);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prev = (i - 1 + tabs.length) % tabs.length;
          tabs[prev].focus();
          activate(prev);
        }
      });
    });
  });

  /* ============== Quantity inputs ============== */
  Array.from(doc.querySelectorAll('[data-quantity-input]')).forEach(function (wrap) {
    const field = wrap.querySelector('[data-quantity-field]');
    const dec = wrap.querySelector('[data-quantity-decrease]');
    const inc = wrap.querySelector('[data-quantity-increase]');
    if (!field) return;
    const min = parseInt(field.getAttribute('min'), 10) || 1;
    function setVal(v) {
      if (v < min) v = min;
      field.value = v;
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (dec) dec.addEventListener('click', function () {
      setVal((parseInt(field.value, 10) || min) - 1);
    });
    if (inc) inc.addEventListener('click', function () {
      setVal((parseInt(field.value, 10) || min) + 1);
    });
  });

  /* ============== Collection sort: submit on change ============== */
  Array.from(doc.querySelectorAll('[data-collection-sort]')).forEach(function (form) {
    const select = form.querySelector('[data-sort-select]');
    if (!select) return;
    select.addEventListener('change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });

  /* ============== Product media thumbnails ============== */
  function rcResolveMainMedia(btn) {
    const gallery = btn.closest('.product-main__media') || doc;
    return (
      gallery.querySelector('#ProductMediaMain') ||
      doc.getElementById('ProductMediaMain') ||
      gallery.querySelector('[data-product-media-main] .product-media-main__image') ||
      gallery.querySelector('[data-product-media-main] img') ||
      gallery.querySelector('.product-media-main__image')
    );
  }

  function rcSwapMainMedia(btn) {
    const main = rcResolveMainMedia(btn);
    if (!main) return;
    const src = btn.getAttribute('data-media-src');
    const alt = btn.getAttribute('data-media-alt') || '';
    if (src) {
      // Strip responsive/intrinsic attributes first so the browser cannot
      // keep selecting the previous image from srcset, then point both
      // src and srcset at the chosen image so the swap actually paints.
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
      main.removeAttribute('width');
      main.removeAttribute('height');
      main.setAttribute('src', src);
      main.setAttribute('srcset', src);
      main.setAttribute('alt', alt);
    }
    const scope = btn.closest('.product-media-thumbs') || doc;
    Array.from(scope.querySelectorAll('[data-media-thumb]')).forEach(function (b) {
      b.classList.remove('is-active');
    });
    btn.classList.add('is-active');
  }

  // Event delegation on the document: fires regardless of when thumbs are
  // added and is immune to per-element binding order/failures.
  doc.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest ? e.target.closest('[data-media-thumb]') : null;
    if (!btn) return;
    e.preventDefault();
    rcSwapMainMedia(btn);
  });

  /* ============== Variant selection: update hidden input ============== */
  Array.from(doc.querySelectorAll('[data-product-form]')).forEach(function (form) {
    const data = (function () {
      try { return JSON.parse(form.getAttribute('data-product') || 'null'); }
      catch (e) { return null; }
    })();
    if (!data || !data.variants) return;

    const radios = Array.from(form.querySelectorAll('[data-option-input]'));
    const hidden = form.querySelector('[data-variant-id]');
    const atc = form.querySelector('[data-add-to-cart]');

    function readSelected() {
      const selected = [];
      const groups = {};
      radios.forEach(function (r) {
        const pos = parseInt(r.getAttribute('data-option-position'), 10);
        if (r.checked) groups[pos] = r.value;
      });
      const positions = Object.keys(groups).map(Number).sort();
      positions.forEach(function (p) { selected.push(groups[p]); });
      return selected;
    }

    function match() {
      const sel = readSelected();
      return data.variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === sel[i]; });
      });
    }

    function update() {
      const variant = match();
      if (!variant) {
        if (atc) {
          atc.setAttribute('disabled', 'disabled');
          atc.setAttribute('aria-disabled', 'true');
          atc.textContent = 'Unavailable';
        }
        return;
      }
      if (hidden) hidden.value = variant.id;
      if (atc) {
        if (variant.available) {
          atc.removeAttribute('disabled');
          atc.removeAttribute('aria-disabled');
          atc.textContent = 'Add to cart';
        } else {
          atc.setAttribute('disabled', 'disabled');
          atc.setAttribute('aria-disabled', 'true');
          atc.textContent = 'Sold out';
        }
      }
    }

    radios.forEach(function (r) {
      r.addEventListener('change', update);
    });
  });

  /* ============== Quick view placeholder ============== */
  Array.from(doc.querySelectorAll('[data-quick-view]')).forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const card = btn.closest('[data-product-card]');
      const link = card ? card.querySelector('a.product-card__media') : null;
      if (link) window.location.href = link.getAttribute('href');
    });
  });
})();
